import ApplyConversationActionRequest from 'owa-service/lib/contract/ApplyConversationActionRequest';
import ApplyConversationActionResponseMessage from 'owa-service/lib/contract/ApplyConversationActionResponseMessage';
import ConversationAction from 'owa-service/lib/contract/ConversationAction';
import TargetFolderId from 'owa-service/lib/contract/TargetFolderId';
import applyConversationActionRequest from 'owa-service/lib/factory/applyConversationActionRequest';
import conversationAction from 'owa-service/lib/factory/conversationAction';
import folderIdFactory from 'owa-service/lib/factory/folderId';
import itemId from 'owa-service/lib/factory/itemId';
import targetFolderIdFactory from 'owa-service/lib/factory/targetFolderId';
import applyConversationActionOperation from 'owa-service/lib/operation/applyConversationActionOperation';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';

/**
 * Create a ConversationAction with ignore params for a given conversation.
 * @param conversationContext the conversation context to be ignored
 * @param currentFolderTargetId - the table view the conversations are moving from (null for search)
 * @param shouldIgnore - whether to ignore the conversation or not
 */
function createIgnoreConversationAction(
    conversationId: string,
    currentFolderTargetId: TargetFolderId,
    shouldIgnore: boolean
): ConversationAction {
    return conversationAction({
        Action: 'AlwaysDelete',
        ConversationId: itemId({ Id: conversationId }),
        ContextFolderId: currentFolderTargetId,
        EnableAlwaysDelete: shouldIgnore,
    });
}

/**
 * Worker method which converts the provided data into an ApplyConversationActionRequest.
 * @param conversationContexts the conversation contexts to be ignored
 * @param currentFolderId - the folder they originate in -- null for search scenarios
 * @param shouldIgnore - whether to ignore the conversation or not
 * @returns an ApplyConversationActionRequest
 */
function configureRequestBody(
    conversationIds: string[],
    currentFolderId: string,
    shouldIgnore: boolean
): ApplyConversationActionRequest {
    // This is a workaround for the fact that in search scenarios we need to pass a completely null ContextFolderId
    // (not the BaseFolderId form in the false condition of the ternary) in all conversationActions.
    const currentFolderTargetId =
        currentFolderId == null
            ? null
            : targetFolderIdFactory({ BaseFolderId: folderIdFactory({ Id: currentFolderId }) });

    const conversationActions = conversationIds.map(conversationId =>
        createIgnoreConversationAction(conversationId, currentFolderTargetId, shouldIgnore)
    );

    return applyConversationActionRequest({
        ConversationActions: conversationActions,
        ReturnMovedItemIds: true,
    });
}

/**
 * The service which calls the ApplyConversationAction with ignore parameters.
 * @param conversationContexts the conversation contexts to be ignored
 * @param currentFolderId - the folder they originate in -- null for search scenarios
 * @param shouldIgnore - whether to ignore the conversation or not
 */
export default function ignoreConversationsService(
    conversationIds: string[],
    currentFolderId: string,
    shouldIgnore: boolean
): Promise<ApplyConversationActionResponseMessage> {
    const requestBody = configureRequestBody(conversationIds, currentFolderId, shouldIgnore);

    return applyConversationActionOperation({
        Header: getJsonRequestHeader(),
        Body: requestBody,
    }).then(response => {
        return response.Body.ResponseMessages.Items[0];
    });
}
